import {
  hasLevel,
  ladderAccess,
  notify,
  notifyFailure,
  quickCheckins,
  type SearchPerson,
  useAccess,
  useCheckinStations,
  useCurrentService,
  useHouseholdSearch,
  useOnlineStatus,
  useStationCheckin,
} from '@church/data'
import {
  faArrowUpRightFromSquare,
  faChevronDown,
  faChildReaching,
  faCircleCheck,
  faHandshakeAngle,
  faMagnifyingGlass,
  faTriangleExclamation,
  faUsers,
} from '@church/ui-icons'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { CheckinLabel, PrintButton, PrintableLabels } from '../components/CheckinLabel.tsx'
import { HistoryPanel } from '../components/checkin/HistoryPanel.tsx'
import { RoomsPanel } from '../components/checkin/RoomsPanel.tsx'
import { SetupPanel } from '../components/checkin/SetupPanel.tsx'
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  InlineSpinner,
  Input,
  PageHeader,
  pressable,
  Select,
  SidePanel,
  Spinner,
  Tabs,
} from '../components/ui.tsx'
import { useTabParam } from '../lib/tabs.ts'

/**
 * Sunday morning.
 *
 * The design constraint that shapes everything: **a check-in must complete
 * without the network**. The id and the security code are generated here, the
 * record goes into a durable queue before anything is sent, and the label
 * renders from what the device already knows. The status strip says plainly
 * whether the queue is empty, because a volunteer needs to know before they
 * pack up whether anything is still waiting to go.
 *
 * Nothing on this screen asks the server a question it cannot answer offline
 * except the search, and a search that fails leaves the last result on screen
 * rather than clearing it.
 */
export const Route = createFileRoute('/_authed/_shell/checkin')({
  beforeLoad: ({ context }) => {
    if (!hasLevel(ladderAccess(context.me), 'checkin', 'viewer')) throw redirect({ to: '/' })
  },
  component: CheckinScreen,
})

const TABS = ['Station', 'Rooms', 'History', 'Setup'] as const
type Tab = (typeof TABS)[number]

function CheckinScreen() {
  const access = useAccess()
  const tabs = access.has('checkin', 'administrator') ? TABS : (['Station', 'Rooms'] as const)
  const [tab, setTab] = useTabParam<Tab>(tabs)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Check-in"
        action={
          <Link
            to="/kiosk"
            className="inline-flex items-center gap-2 text-sm font-medium text-content-brand"
          >
            Kiosk mode
            <Icon icon={faArrowUpRightFromSquare} className="size-3" />
          </Link>
        }
      />
      <Tabs tabs={tabs} value={tab} onChange={setTab}>
        {tab === 'Station' && <StationPanel />}
        {tab === 'Rooms' && <RoomsPanel />}
        {tab === 'History' && <HistoryPanel />}
        {tab === 'Setup' && <SetupPanel />}
      </Tabs>
    </div>
  )
}

/**
 * Whether anything is still waiting to reach the server. All saved: a quiet
 * line. Anything waiting, or no network: a strip that can't be missed,
 * because a volunteer needs to know before they pack up.
 */
function SyncStatus({ queued }: { queued: number }) {
  const online = useOnlineStatus()

  if (queued === 0 && online)
    return (
      <span className="flex items-center gap-1.5 text-sm text-content-muted">
        <span aria-hidden className="size-2 rounded-full bg-success-solid" />
        <span data-testid="sync-status">Everything is saved</span>
      </span>
    )
  return (
    <div
      role="status"
      className={`flex w-full items-center gap-3 rounded-control px-3 py-2 text-sm ${
        queued > 0 ? 'bg-warning-soft text-warning-content' : 'bg-surface-sunken text-content-muted'
      }`}
    >
      <Icon icon={faTriangleExclamation} />
      <span data-testid="sync-status">
        {queued > 0
          ? `${queued} check-in${queued === 1 ? '' : 's'} still to send${online ? ', sending…' : ' — the network is down; they’re safe on this device'}`
          : 'Offline. Check-ins are being kept on this device and sent when it’s back.'}
      </span>
    </div>
  )
}

function StationPanel() {
  const { data: stations } = useCheckinStations()
  const [stationId, setStationId] = useState('')
  const usable = stations?.filter((entry) => entry.isActive) ?? []
  const active = stationId || usable[0]?.id || ''

  if (stations === undefined) return <Spinner />
  if (usable.length === 0) {
    return (
      <EmptyState
        title="No station registered"
        hint="Register one under Setup, then this becomes the check-in desk."
      />
    )
  }

  return <Desk stationId={active} stations={usable} onStation={setStationId} />
}

/**
 * The desk: the search first (it is what the volunteer came for), then where
 * this desk is checking in to, as one line that opens a panel to change it.
 */
function Desk({
  stationId,
  stations,
  onStation,
}: {
  stationId: string
  stations: { id: string; name: string }[]
  onStation: (id: string) => void
}) {
  const [term, setTerm] = useState('')
  const [submitted, setSubmitted] = useState('')
  const [choosing, setChoosing] = useState(false)
  const current = useCurrentService()
  const timeId = current.service?.id ?? null
  const {
    data: households,
    isFetching,
    error,
    refetch,
  } = useHouseholdSearch(submitted, submitted !== '', timeId)
  const { checkIn, pending, queued } = useStationCheckin(stationId)
  const [allBusy, setAllBusy] = useState<string | null>(null)
  const stationName = stations.find((entry) => entry.id === stationId)?.name ?? 'This station'
  const serviceName =
    current.service?.name ?? (current.times.length > 0 ? 'Every room' : 'No service times')

  const checkInOne = (
    person: SearchPerson,
    location: { id: string; name: string },
    serving?: boolean,
  ) =>
    checkIn.mutateAsync({ person, location, timeId, serving }).then((label) => {
      notify({
        title: serving
          ? `${person.firstName} is serving in ${location.name}`
          : `${person.firstName} checked in to ${location.name}`,
        description: serving
          ? 'Their badge is ready to print.'
          : `Pickup code ${label.securityCode}. The label is ready to print.`,
      })
      return label
    })

  return (
    <div className="space-y-4">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          setSubmitted(term.trim())
        }}
      >
        <Input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Surname or phone number"
          className="flex-1 text-lg"
          data-testid="checkin-search"
          aria-label="Find a family"
          autoFocus
        />
        <IconButton type="submit" icon={faMagnifyingGlass} label="Find" variant="primary" />
      </form>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          onClick={() => setChoosing(true)}
          className={`${pressable} inline-flex min-h-10 items-center gap-2 rounded-full border border-border-default bg-surface-raised px-3 text-sm text-content-default hover:bg-surface-sunken`}
        >
          <span className="font-medium">{stationName}</span>
          <span className="text-content-muted">· {serviceName}</span>
          <Icon icon={faChevronDown} className="size-3 text-content-muted" />
        </button>
        <SyncStatus queued={queued} />
      </div>

      <ErrorNote
        error={error}
        onRetry={() => void refetch()}
        retrying={isFetching}
        safe="Check-ins already taken are safe on this device."
      />
      {isFetching && !households && <Spinner label="Finding families…" />}

      {households?.map((household) => {
        const quick = quickCheckins(household.members)
        return (
          <Card bleed key={household.householdId} className="space-y-3 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-content-default">
                {household.householdName ?? 'Household'}
              </h3>
              {quick.length >= 2 && (
                <Button
                  icon={faUsers}
                  pending={allBusy === household.householdId}
                  pendingLabel="Checking in…"
                  onClick={async () => {
                    setAllBusy(household.householdId)
                    try {
                      for (const entry of quick)
                        await checkIn.mutateAsync({
                          person: entry.person,
                          location: entry.location,
                          timeId,
                        })
                      notify({
                        title: `Checked in ${quick.length}: ${quick
                          .map((entry) => `${entry.person.firstName} → ${entry.location.name}`)
                          .join(', ')}`,
                        description: 'Their labels are ready to print.',
                      })
                    } catch (failure) {
                      notifyFailure(failure, undefined, 'Couldn’t check everyone in')
                    } finally {
                      setAllBusy(null)
                    }
                  }}
                >
                  Check in all ({quick.length})
                </Button>
              )}
            </div>
            {household.members.map((person) => (
              <PersonRow
                key={person.personId}
                person={person}
                onCheckIn={(location, serving) => {
                  void checkInOne(person, location, serving).catch(() => undefined)
                }}
                busy={
                  allBusy === household.householdId ||
                  (checkIn.isPending && checkIn.variables?.person.personId === person.personId)
                }
              />
            ))}
          </Card>
        )
      })}

      {submitted !== '' && households?.length === 0 && !isFetching && (
        <EmptyState
          title={`Nobody found for “${submitted}”`}
          hint="Try the surname alone, or the phone number on file."
        />
      )}

      <ErrorNote error={checkIn.error} safe="Nobody else’s check-in was affected." />

      {pending.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-content-default">Labels</h3>
            <PrintButton />
          </div>
          {pending.map((label) => (
            <CheckinLabel key={label.clientId} label={label} />
          ))}
          <PrintableLabels labels={pending} />
        </section>
      )}

      <SidePanel open={choosing} onOpenChange={setChoosing} title="Where this desk checks in">
        {stations.length > 1 && (
          <Field
            label="Station"
            description="Labels print here, and check-ins are recorded against it."
          >
            <Select value={stationId} onChange={(event) => onStation(event.target.value)}>
              {stations.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {current.times.length > 0 ? (
          <Field label="Service" description="Only its rooms are offered.">
            <Select
              value={current.chosenId ?? ''}
              onChange={(event) => current.choose(event.target.value || null)}
            >
              <option value="">
                {current.automatic
                  ? `${current.automatic.name} (now)`
                  : 'Every room (none running)'}
              </option>
              {current.times.map((time) => (
                <option key={time.id} value={time.id}>
                  {time.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <p className="text-sm text-content-muted">
            No service times are set up, so every room is offered. Add times under Setup.
          </p>
        )}
        <Link
          to="/kiosk"
          className="inline-flex items-center gap-2 text-sm font-medium text-content-brand"
        >
          Open kiosk mode on this device
          <Icon icon={faArrowUpRightFromSquare} className="size-3" />
        </Link>
      </SidePanel>
    </div>
  )
}

function PersonRow({
  person,
  onCheckIn,
  busy,
}: {
  person: SearchPerson
  onCheckIn: (location: { id: string; name: string }, serving?: boolean) => void
  busy: boolean
}) {
  const open = person.openCheckin

  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-border-subtle pt-3 first:border-0 first:pt-0">
      <div className="min-w-48 flex-1">
        <p className="text-base font-medium text-content-default">
          {person.firstName} {person.lastName}
        </p>
        <p className="text-sm text-content-muted">
          {[
            person.isChild ? 'child' : person.role,
            person.ageMonths !== null ? `${Math.floor(person.ageMonths / 12)}y` : null,
            person.grade !== null ? `grade ${person.grade}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {person.labelNotes && (
          <p className="mt-1.5">
            <Chip tone="danger" size="md">
              <Icon icon={faTriangleExclamation} className="size-3" />
              {person.labelNotes}
            </Chip>
          </p>
        )}
      </div>

      {open ? (
        <Chip tone="success" size="md">
          <Icon
            icon={open.kind === 'volunteer' ? faHandshakeAngle : faCircleCheck}
            className="size-3"
          />
          {open.kind === 'volunteer' ? 'Serving in' : 'In'} {open.locationName}
        </Chip>
      ) : person.eligibleLocations.length === 0 && person.serveLocations.length === 0 ? (
        <span className="text-sm text-content-muted">
          {person.isChild ? 'No room matches their age' : 'No room to check in to'}
        </span>
      ) : busy ? (
        <span className="inline-flex items-center gap-2 text-sm text-content-muted" role="status">
          <InlineSpinner className="size-4 text-content-brand" />
          Checking in…
        </span>
      ) : (
        <div className="flex flex-wrap gap-2">
          {person.eligibleLocations.map((location) => (
            <Button
              key={location.id}
              variant="secondary"
              icon={faChildReaching}
              disabled={location.isFull}
              title={location.isFull ? `${location.name} is full` : undefined}
              onClick={() => onCheckIn({ id: location.id, name: location.name })}
            >
              {location.name}
              {location.isFull ? ' — full' : ''}
            </Button>
          ))}
          {person.serveLocations.map((location) => (
            <Button
              key={`serve-${location.id}`}
              variant="ghost"
              icon={faHandshakeAngle}
              onClick={() => onCheckIn({ id: location.id, name: location.name }, true)}
            >
              Serve in {location.name}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}
